// serviceWorkerRegistration.js
import axios from 'axios';
import { notification } from 'antd';

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/.test(window.location.hostname);

const hasPushSupport = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export function isAdminPageRoute() {
  if (typeof window === 'undefined') return false;

  const pathname = window.location?.pathname || '';
  return pathname === '/admin' || pathname === '/adminPage' || pathname.startsWith('/adminPage/');
}

export function urlBase64ToUint8Array(b64url) {
  const s = String(b64url || '').trim();
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const base64 = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// стабильный deviceId для текущего браузера / установленного PWA
export function getOrCreateDeviceId() {
  let id = localStorage.getItem('deviceId');
  if (!id) {
    id =
      (crypto.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem('deviceId', id);
  }
  return id;
}

// флажок, чтобы не автопредлагать, если пользователь отключил
export function markPushDisabledLocally(disabled) {
  const key = `pushDisabled:${getOrCreateDeviceId()}`;
  if (disabled) localStorage.setItem(key, '1');
  else localStorage.removeItem(key);
}

export function isPushDisabledLocally() {
  const key = `pushDisabled:${getOrCreateDeviceId()}`;
  return localStorage.getItem(key) === '1';
}

async function syncSubscriptionWithServer(subscription) {
  await axios.post(
    '/subscribe',
    {
      subscription: JSON.parse(JSON.stringify(subscription)),
      deviceId: getOrCreateDeviceId(),
    },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    }
  );
}

export async function autoEnablePushIfPossible() {
  if (isAdminPageRoute()) return false;
  if (!hasPushSupport()) return false;
  if (isPushDisabledLocally()) return false;

  if (Notification.permission !== 'granted') {
    return false;
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();

  if (!sub) {
    const { data } = await axios.get('/vapidPublicKey', { withCredentials: true });
    const appKey = urlBase64ToUint8Array(String(data?.publicKey || ''));

    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    });
  }

  // ВАЖНО для iOS PWA:
  // не только создаем подписку, но и ВСЕГДА отправляем текущую подписку на сервер.
  // Установленное PWA может иметь отдельное хранилище/endpoint от браузера.
  await syncSubscriptionWithServer(sub);

  await axios.post(
    '/notifications/enabled',
    { deviceId: getOrCreateDeviceId(), enabled: true },
    { withCredentials: true }
  );

  return true;
}

// маленькие шорткаты для antd notification
const notify = {
  info: (title, description) =>
    notification.info({ message: title, description, placement: 'bottomRight' }),
  success: (title, description) =>
    notification.success({ message: title, description, placement: 'bottomRight' }),
  error: (title, description) =>
    notification.error({ message: title, description, placement: 'bottomRight' }),
};

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('Это приложение обслуживается кэшем Service Worker.');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // активно проверяем обновления
      try {
        registration.update();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') registration.update();
        });
        setInterval(() => registration.update(), 60 * 60 * 1000);
      } catch { }

      // один раз перезагрузим страницу при смене контролирующего SW.
      // При первом заходе SW ещё не управлял страницей: skipWaiting + clients.claim
      // тоже дают controllerchange, и новичок с рекламы видел, как лендинг загружается
      // второй раз. Перезагружаем только при обновлении уже работавшего SW.
      const hadController = Boolean(navigator.serviceWorker.controller);
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController || reloaded) return;
        reloaded = true;
        window.location.reload();
      });

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('Новое обновление доступно.');
              showUpdateNotification(() => {
                registration.waiting?.postMessage?.('SKIP_WAITING');
                registration.active?.postMessage?.('SKIP_WAITING');
              });
              if (config?.onUpdate) config.onUpdate(registration);
            } else {
              console.log('Контент кэширован для оффлайн-использования.');
              if (config?.onSuccess) config.onSuccess(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Ошибка при регистрации Service Worker:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('Нет соединения с интернетом. Приложение работает в оффлайн-режиме.');
    });
}

/**
 * Вызывай эту функцию из страницы «Настройки профиля» по кнопке.
 * Пример:
 * const reg = await navigator.serviceWorker.ready;
 * await enablePushFromSettings(reg);
 */
export async function enablePushFromSettings(registration) {
  if (isAdminPageRoute()) return;

  if (!hasPushSupport()) {
    notify.error('Не поддерживается', 'Уведомления не поддерживаются на этом устройстве/в этом браузере');
    return;
  }

  const perm = Notification.permission; // 'default' | 'granted' | 'denied'

  if (perm === 'denied') {
    notify.error(
      'Уведомления запрещены',
      'Разрешите их в настройках сайта, затем вернитесь и нажмите «Включить».'
    );
    return;
  }

  const proceed = async () => {
    try {
      const reg = registration || (await navigator.serviceWorker.ready);
      let subscription = await reg.pushManager.getSubscription();

      if (!subscription) {
        const { data } = await axios.get('/vapidPublicKey', { withCredentials: true });
        const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // ВАЖНО: синхронизируем с сервером всегда.
      await sendSubscriptionToServer(subscription);

      await axios.post(
        '/notifications/enabled',
        { deviceId: getOrCreateDeviceId(), enabled: true },
        { withCredentials: true }
      );

      markPushDisabledLocally(false);
      notify.success('Готово', 'Уведомления включены');
    } catch (e) {
      console.error('Ошибка подписки:', e);
      notify.error('Ошибка', 'Не удалось включить уведомления');
    }
  };

  if (perm === 'granted') {
    await proceed();
    return;
  }

  // 'default' → спрашиваем разрешение по клику пользователя
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await proceed();
    } else if (permission === 'denied') {
      notify.error(
        'Уведомления запрещены',
        'Разрешите их в настройках сайта, затем попробуйте снова.'
      );
    }
  } catch (err) {
    console.error('requestPermission error:', err);
    notify.error('Ошибка', 'Не удалось запросить разрешение на уведомления');
  }
}

// Оставлено для совместимости — можно не использовать
export function subscribeToPushNotifications(registration) {
  return enablePushFromSettings(registration);
}

function sendSubscriptionToServer(subscription) {
  return syncSubscriptionWithServer(subscription)
    .then((res) => {
      console.log('Подписка успешно отправлена на сервер');
      return res;
    })
    .catch((err) => {
      console.error('Ошибка при отправке подписки на сервер:', err);
      throw err;
    });
}

// Плашка «Есть обновление»
function showUpdateNotification(onConfirm) {
  notification.info({
    message: 'Доступна новая версия приложения',
    description: 'Нажмите «Обновить», чтобы применить изменения.',
    placement: 'bottomRight',
    duration: 0,
  });

  const ok = window.confirm('Доступна новая версия приложения. Обновить сейчас?');
  if (ok) onConfirm?.();
}


/**
 * Получить endpoint текущей push-подписки.
 * Нужен для logout, чтобы сервер удалил именно эту подписку из MongoDB.
 */
export async function getCurrentPushEndpoint() {
  try {
    if (!hasPushSupport()) {
      return null;
    }

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();

    return sub?.endpoint || null;
  } catch (error) {
    console.error('getCurrentPushEndpoint error:', error);
    return null;
  }
}


/**
 * Logout с удалением push-подписки текущего устройства.
 *
 * Используй вместо:
 * await axios.get('/logout/api', { withCredentials: true });
 */
export async function logoutWithPushRemove() {
  const endpoint = await getCurrentPushEndpoint();

  return axios.post(
    '/logout/api',
    {
      deviceId: getOrCreateDeviceId(),
      endpoint,
    },
    {
      withCredentials: true,
    }
  );
}


/**
 * Завершение текущего активного сеанса с удалением push-подписки.
 *
 * Используй вместо обычного axios.post('/active-session/terminate-current/api', ...)
 */
export async function terminateCurrentSessionWithPushRemove() {
  const endpoint = await getCurrentPushEndpoint();

  return axios.post(
    '/active-session/terminate-current/api',
    {
      deviceId: getOrCreateDeviceId(),
      endpoint,
    },
    {
      withCredentials: true,
    }
  );
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error('Ошибка при отмене регистрации Service Worker:', error));
  }
}
